import * as React from 'react';
import { NumberField, RaRecord, useNotify, useRedirect } from 'react-admin';
import {
    Box,
    Typography,
    Button,
    Stack,
    TextField,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    CircularProgress,
    FormHelperText,
    InputAdornment,
    FormControlLabel,
} from '@mui/material';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { Formik, Form } from 'formik';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { IconButton } from '../../components/icon-button/IconButton.tsx';
import EditIcon from '@mui/icons-material/Edit';
import { TypeOfCuisine } from '../../enums/TypeOfCuisine.ts';
import { Language } from '../../enums/Language.ts';
import TranslateService from "../../services/translate/translateService.ts";
import { WeekDay } from "../../enums/weekDay.ts";
import { LocationType } from "../../enums/LocationType.ts";
import { RestaurantService } from "../../services/restaurant/restaurantService.ts";
import { LocationService, type City } from "../../services/location/locationService.ts";
import { useParams } from "react-router-dom";
import { useJsApiLoader, StandaloneSearchBox, Autocomplete } from "@react-google-maps/api";
import { ImageCropDialog } from "../../components/dialogs/ImageCropDialog.tsx";
import * as Sentry from '@sentry/react';
import { isAdmin } from '../../core/roles.ts';
import { isShabbatOnlyLogin } from '../../core/shabbatOnly.ts';
import { Switch } from '../../components/switch/Switch.tsx';
import { loadShabbatVenues } from '../shabbat/shabbatShared.tsx';
import ShabbatHostLogin from '../shabbat/ShabbatHostLogin.tsx';


const TYPE_OF_CUISINE = Object.entries(TypeOfCuisine).map(([id, name]) => ({
    id,
    name,
}));

const LANGUAGE = Object.entries(Language).map(([id, name]) => ({
    id,
    name,
}));

const LOCATION_TYPE = Object.entries(LocationType).map(([id, name]) => ({ id, name }))

const GOOGLE_MAPS_LIBRARIES: ("places")[] = ['places'];

export default function LocationsCreate(): JSX.Element {
    const { t } = useTranslation();
    const translateService = new TranslateService();
    const [locationTypes, setLocationTypes] = React.useState<any[]>([]);
    const [isTranslating, setIsTranslating] = React.useState(false);
    const [translated, setTranslated] = React.useState(false);
    const [translations, setTranslations] = React.useState<any[]>([]);
    const [isSaving, setIsSaving] = React.useState(false);
    const [isLoading, setIsLoading] = React.useState(false);
    const [data, setData] = React.useState<RaRecord | null>(null);
    const [address, setAddress] = React.useState(null);
    const [isAddressComplete, setIsAddressComplete] = React.useState(false);

    const addressInputRef = React.useRef<StandaloneSearchBox>(null);

    const { isLoaded } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_PLACES_API_KEY,
        libraries: GOOGLE_MAPS_LIBRARIES,
    })

    console.log(isLoaded);

    const { id } = useParams();
    const isEditMode = Boolean(id);

    const restaurantService = new RestaurantService();

    React.useEffect(() => {
        if (isEditMode) {
            setIsLoading(true);
            (async () => {
                try {
                    const data = await restaurantService.getRestaurants(id);
                    console.log("data: " + JSON.stringify(data));
                    setData(data);
                    setPreviewUrl(data.imageUrl);
                    setTranslations(data.descriptionTranslations || []);


                    const addr = data.address;

                    const formatted = {
                        streetWithNo: addr.streetWithNo || "",
                        postalCode: addr.postalCode || "",
                        city: addr.city || "",
                        state: addr.state || "",
                        district: addr.district || "",
                        latitude: addr.latitude?.toString() || "",
                        longitude: addr.longitude?.toString() || "",
                    };

                    setAddress(formatted);



                    setIsLoading(false);
                } catch (error) {
                    notify(error.response.data.error, { type: 'error' });
                    console.error(error.response.data.error);
                    setIsLoading(false);
                    Sentry.captureException(error);
                }
            })();
        }
    }, [isEditMode, id]);

    //TODO: it will be needed later when location types endpoint will be ready
    // React.useEffect(() => {
    //     let isActive = true;
    //     (async () => {
    //         try {
    //             const res = await locationService.getLocationTypes();
    //             console.log(res);
    //             if (isActive) setLocationTypes(res);
    //         } catch (e) {
    //             console.error('Failed to load location types', e);
    //             Sentry.captureException(e);
    //         }
    //     })();
    //     return () => { isActive = false; };
    // }, []);

    // Cities to put this restaurant in. The admin list, not the customer one,
    // so a restaurant can be loaded into a market before that market is opened.
    // A failure here leaves the dropdown empty rather than blocking the form:
    // every other field still saves.
    const [cities, setCities] = React.useState<City[]>([]);

    React.useEffect(() => {
        let isActive = true;
        (async () => {
            try {
                const res = await new LocationService().getCities();
                if (isActive) setCities(res);
            } catch (e) {
                console.error('Failed to load cities', e);
                Sentry.captureException(e);
            }
        })();
        return () => { isActive = false; };
    }, []);

    const [fileBlob, setFileBlob] = React.useState<Blob | null>(null);
    const [previewUrl, setPreviewUrl] = React.useState<string | null>(null);
    const [cropDialogOpen, setCropDialogOpen] = React.useState(false);
    const [rawImageSrc, setRawImageSrc] = React.useState<string | null>(null);
    const notify = useNotify();
    const redirect = useRedirect();

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        const file = e.dataTransfer.files?.[0];
        if (file) handleFile(file);
    };

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) handleFile(file);
        e.target.value = '';
    };

    const handleFile = (file: File) => {
        const reader = new FileReader();
        reader.onload = () => {
            setRawImageSrc(reader.result as string);
            setCropDialogOpen(true);
        };
        reader.readAsDataURL(file);
    };

    const handleCropComplete = (croppedBlob: Blob) => {
        setFileBlob(croppedBlob);
        setPreviewUrl(URL.createObjectURL(croppedBlob));
        setCropDialogOpen(false);
    };

    const handleCropCancel = () => {
        setCropDialogOpen(false);
        if (!fileBlob) {
            setRawImageSrc(null);
        }
    };

    const handleRemove = () => {
        setFileBlob(null);
        setPreviewUrl(null);
        setRawImageSrc(null);
    };

    const days = Object.entries(WeekDay).map(([id, name]) => ({ id, name }));

    // Where a restaurant is paid out, its commission and its delivery venue
    // are Vello's to set, so only admins see or send them. Asked positively,
    // like the rest of roles.ts: a role added later does not get them by
    // default. The server refuses a restaurant login's change to any of them
    // and does not send them to one, so for a restaurant they arrive blank.
    const showsMoneyFields = isAdmin();
    const shabbatOnly = isShabbatOnlyLogin();

    const validationSchema = Yup.object({
        name: Yup.string().required('Name is required'),
        venueId: Yup.string(),
        email: Yup.string().email('Invalid email').required('Email is required'),
        phoneNumber: Yup.string()
            .required('Phone number is required')
            .matches(/^\+[0-9]{2,4}[0-9]{5,12}$/, 'Invalid phone number format'),
        language: Yup.string().required('Language is required'),
        typeOfCuisine: Yup.string().required('Cuisine type is required'),
        description: Yup.string().required('Description is required'),
        // Not validated at all for a restaurant: the field is not shown to
        // one, and a blank value (which is what the server now sends a
        // restaurant) failed Yup.number() and blocked every save.
        restaurantFee: showsMoneyFields
            ? Yup.number()
                .required('The delivery commission is required')
                .min(0, 'Min value is 0')
                .max(100, 'Max value is 100')
            : Yup.mixed().notRequired(),
        // The second rate (backend feature/pickup): pickup and dine-in orders
        // are charged this one, delivery orders the one above. Optional: left
        // blank on create the server uses the delivery rate.
        pickupCommissionRate: showsMoneyFields
            ? Yup.number()
                .typeError('Enter a percentage')
                .min(0, 'Min value is 0')
                .max(100, 'Max value is 100')
            : Yup.mixed().notRequired(),
        // Vello's total fee on Shabbat registrations (Ariel, 15 Oct 2026: 4%,
        // the 1.4% card fee included). Admins only, like the commission.
        shabbatFeePercent: showsMoneyFields
            ? Yup.number()
                .typeError('Enter a percentage')
                .required('The Shabbat meals fee is required')
                .min(0, 'Min value is 0')
                .max(100, 'Max value is 100')
                .test('two-decimals', 'At most 2 decimals', v => v == null || /^\d+(\.\d{1,2})?$/.test(String(v)))
            : Yup.mixed().notRequired(),
        addressObject: Yup.object()
            .required('Address is required')
            .shape({
                streetWithNo: Yup.string().required('Street is missing'),
                postalCode: Yup.string().required('Postal code is missing'),
                city: Yup.string().required('City is missing'),
                state: Yup.string().required('State is missing'),
                latitude: Yup.string().required(),
                longitude: Yup.string().required(),
            }),
    });

    const initialValues = {
        locationType: '',
        typeOfCuisine: '',
        name: '',
        venueId: '',
        phoneNumber: '',
        email: '',
        address: '',
        addressObject: {
            streetWithNo: '',
            postalCode: '',
            city: '',
            state: '',
            district: '',
            latitude: '',
            longitude: ''
        },
        language: '',
        description: '',
        restaurantFee: '',
        // Pickup & dine-in commission %. Blank = same as the delivery rate.
        pickupCommissionRate: '',
        subMerchantId: '',
        // Empty string rather than null for the same reason as isHidden below:
        // Formik must not switch the field from uncontrolled to controlled.
        cityId: '',
        // Present in create mode too, so Formik does not switch the field from
        // uncontrolled to controlled once a restaurant exists.
        isHidden: false,
        // Shabbat meals. Admins only, like the money fields: whether a venue
        // hosts meals, and whether it is a Chabad House that does nothing else.
        hostsShabbat: false,
        shabbatOnly: false,
        shabbatFeePercent: '4',
        // Dine-in orders (customer orders now, eats at the venue). Admins only.
        acceptsDineIn: false,
        // Pickup orders (order now, collect at the counter). Admins only.
        acceptsPickup: false,
        openingHours: days.reduce((acc, day) => {
            acc[day.id] = { openingTime: '00:00', closingTime: '00:00' };
            return acc;
        }, {} as Record<string, { openingTime: string; closingTime: string }>)
    };

    const initialEditValues = isEditMode && data ? {
        locationType: 'RESTAURANT', //TODO: replace with real value when backend will be ready for location types
        typeOfCuisine: data.cuisineType || '',
        name: data.name || '',
        venueId: data.venueId || '',
        phoneNumber: data.phoneNumber || '',
        email: data.email || '',
        address: data.address?.streetWithNo + " " + data.address?.city + " " + data.address?.postalCode || '',

        addressObject: {
            streetWithNo: data.address?.streetWithNo || "",
            postalCode: data.address?.postalCode || "",
            city: data.address?.city || "",
            state: data.address?.state || "",
            district: data.address?.district || "",
            latitude: data.address?.latitude?.toString() || "",
            longitude: data.address?.longitude?.toString() || "",
        },

        subMerchantId: data.subMerchantId || '',
        // ?? rather than ||, so a city is shown as chosen even though ids are
        // numbers and 0 would otherwise read as absent.
        cityId: data.cityId ?? '',
        language: data.language || '',
        description: data.description || '',
        // Rendered from the server rather than defaulted, so reopening the page
        // shows whether the restaurant is actually hidden.
        isHidden: Boolean(data.isHidden),
        restaurantFee: data.commissionFee ?? '',
        // Admin only: absent for a restaurant login.
        pickupCommissionRate: data.pickupCommissionFee ?? '',
        hostsShabbat: Boolean(data.hostsShabbat),
        shabbatOnly: Boolean(data.shabbatOnly),
        acceptsDineIn: Boolean((data as any).acceptsDineIn),
        acceptsPickup: Boolean((data as any).acceptsPickup),
        // Only an admin is sent it; a restaurant's page leaves it out.
        shabbatFeePercent: data.shabbatFeePercent === undefined || data.shabbatFeePercent === null ? '' : String(Number(data.shabbatFeePercent)),
        openingHours: data.openingHours?.reduce((acc: any, oh: any) => {
            acc[oh.dayOfWeek] = {
                openingTime: oh.openingTime || '00:00',
                closingTime: oh.closingTime || '00:00'
            };
            return acc;
        }, {}) || initialValues.openingHours
    } : initialValues;

    const handleSubmit = async (values: typeof initialValues) => {
        setIsSaving(true);

        const openingHoursData = Object.entries(values.openingHours).map(
            ([weekday, { openingTime, closingTime }]) => {
                const existingHour = data?.openingHours?.find(
                    oh => oh.dayOfWeek === weekday
                );

                return {
                    ...(isEditMode && existingHour?.id && { id: existingHour.id }),
                    weekday: weekday,
                    openingTime,
                    closingTime
                };
            }
        );

        const formData = new FormData();

        console.log("Address:", address);

        // Only an admin's save carries the payout, commission and venue
        // settings; a restaurant's leaves them out, which the server reads as
        // "leave them alone".
        const moneyFields = showsMoneyFields ? {
            venueId: values.venueId,
            ...(values.subMerchantId ? { subMerchantId: values.subMerchantId } : {}),
            commissionRate: values.restaurantFee,
            // The Shabbat switches ride with the admin-only settings: the
            // server refuses a restaurant login's change to either.
            hostsShabbat: Boolean(values.hostsShabbat),
            shabbatOnly: Boolean(values.shabbatOnly),
            // Dine-in is Vello's switch too (admin only on the server).
            acceptsDineIn: Boolean(values.acceptsDineIn),
            acceptsPickup: Boolean(values.acceptsPickup),
            // Blank = leave it alone (update) / use the delivery rate (create).
            ...(values.pickupCommissionRate !== '' && values.pickupCommissionRate !== undefined && values.pickupCommissionRate !== null
                ? { pickupCommissionRate: Number(values.pickupCommissionRate) } : {}),
            ...(values.shabbatFeePercent !== '' && values.shabbatFeePercent !== undefined && values.shabbatFeePercent !== null
                ? { shabbatFeePercent: Number(values.shabbatFeePercent) } : {}),
        } : {};

        const requestPayload = isEditMode ? {
            id: id,
            cuisineType: values.typeOfCuisine,
            name: values.name,
            phoneNumber: values.phoneNumber,
            updateTranslationRequests: [],
            email: values.email,
            language: values.language,
            description: values.description,
            ...moneyFields,
            // Omitted when blank: on update the server reads a missing city as
            // "leave it alone" rather than "clear it".
            ...(values.cityId ? { cityId: Number(values.cityId) } : {}),
            updateAddressRequest: {
                id: data?.address?.id,
                streetWithNo: address.streetWithNo != "undefined undefined" ? address.streetWithNo : 'No Street',
                postalCode: address.postalCode || '',
                city: address.city,
                state: address.state,
                district: address.district,
                latitude: address.latitude,
                longitude: address.longitude
            },
            updateOpeningHoursRequests: openingHoursData,
        } : {
            cuisineType: values.typeOfCuisine,
            name: values.name,
            phoneNumber: values.phoneNumber,
            email: values.email,
            language: values.language,
            description: values.description,
            ...moneyFields,
            ...(values.cityId ? { cityId: Number(values.cityId) } : {}),
            createAddressRequest: {
                streetWithNo: address.streetWithNo,
                postalCode: address.postalCode,
                city: address.city,
                state: address.state,
                district: address.district,
                latitude: address.latitude,
                longitude: address.longitude
            },
            createOpeningHoursRequest: openingHoursData,
        };

        const jsonBlob = new Blob(
            [JSON.stringify(requestPayload)],
            { type: "application/json" }
        );

        formData.append("request", jsonBlob);

        if (fileBlob) {
            formData.append("file", fileBlob);
        } else {
            formData.append("file", new Blob());
        }

        try {
            if (isEditMode) {
                console.log(requestPayload);
                await restaurantService.updateRestaurant(formData);
                notify('Location updated successfully', { type: 'success' });
            } else {
                await restaurantService.createRestaurant(formData);
                notify('Location created successfully', { type: 'success' });
            }
            // The Shabbat pages in the menu follow the venue's switch.
            if (showsMoneyFields) loadShabbatVenues(true);
            setIsSaving(false);
            redirect("/locationsManagement");
        } catch (error) {
            notify(error.response.data.error, { type: 'error' });
            console.error(error.response.data.error);
            Sentry.captureException(error);
            setIsSaving(false);
        }
    };

    //TODO: style it (CircularProgress in the center of the form)
    if (!isLoaded || (isEditMode && isLoading)) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <>
            <Formik
                initialValues={initialEditValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
                enableReinitialize //HERE: it allows to reinitialize form when initialEditValues change
            >
                {({ values, handleChange, errors, touched, isValid, dirty, setFieldValue, handleBlur, setFieldError }) => {


                    const addressError =
                        errors.addressObject?.streetWithNo ||
                        errors.addressObject?.postalCode ||
                        errors.addressObject?.city ||
                        errors.addressObject?.state;


                    const handleOnPlacesChanged = async () => {
                        console.log('changed');
                        const places = addressInputRef.current?.getPlaces();
                        if (places && places.length > 0) {
                            const place = places[0];

                            const lat = place.geometry?.location?.lat();
                            const lng = place.geometry?.location?.lng();


                            const getAddressComponent = (types: string[]) => {
                                const component = place.address_components?.find(comp =>
                                    types.some(type => comp.types.includes(type))
                                );
                                return component?.long_name;
                            };

                            const streetNumber = getAddressComponent(['street_number']);
                            const route = getAddressComponent(['route']);
                            const streetWithNo = `${route} ${streetNumber}`.trim();

                            const addressDetails = {
                                streetWithNo: streetWithNo,
                                postalCode: getAddressComponent(['postal_code']),
                                city: getAddressComponent(['locality']) || getAddressComponent(['postal_town']),
                                state: getAddressComponent(['administrative_area_level_1']),
                                district: getAddressComponent(['sublocality']) || getAddressComponent(['administrative_area_level_2']),
                                latitude: lat.toString(),
                                longitude: lng.toString()
                            };

                            setAddress(addressDetails);
                            setFieldValue("addressObject", addressDetails);

                            setFieldValue("address", place.formatted_address || `${streetWithNo}, ${addressDetails.city}, ${addressDetails.postalCode}`);
                            console.log('Address details:', addressDetails);

                            const isComplete = addressDetails.streetWithNo &&
                                addressDetails.postalCode &&
                                addressDetails.city &&
                                addressDetails.state &&
                                addressDetails.latitude &&
                                addressDetails.longitude;
                            if (isComplete) {
                                setIsAddressComplete(true);
                            }
                        }
                    };

                    return (
                        <Form>
                            <Box
                                sx={{
                                    px: 4,
                                    py: 4,
                                    display: 'flex',
                                    gap: 'var(--space-32)',
                                    flexDirection: 'row'
                                }}
                            >
                                {/* A Chabad House takes no orders: its hours, type and
                                    cuisine mean nothing to it, so it does not see them
                                    (their stored values still go back unchanged). */}
                                {!shabbatOnly && <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        flex: 0.5,
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontWeight: 600,
                                            fontSize: 'var(--text-sm)',
                                            mb: 2,
                                            color: 'text.secondary',
                                        }}
                                    >
                                        {t('locationsManagement.create.openingHours')}
                                    </Typography>

                                    <Stack spacing={3}>
                                        {days.map((day) => (
                                            <Stack
                                                key={day.id}
                                                direction="row"
                                                alignItems="center"
                                                spacing={2}
                                                justifyContent="space-between"
                                            >
                                                <Typography
                                                    sx={{
                                                        flex: 1,
                                                        fontSize: 'var(--text-xs)',
                                                        color: 'text.primary',
                                                        minWidth: 80,
                                                    }}
                                                >
                                                    {day.name}
                                                </Typography>

                                                <TextField
                                                    type="time"
                                                    size="small"
                                                    name={`openingHours.${day.id}.openingTime`}
                                                    value={values.openingHours[day.id].openingTime}
                                                    onChange={handleChange}
                                                />
                                                <TextField
                                                    type="time"
                                                    size="small"
                                                    name={`openingHours.${day.id}.closingTime`}
                                                    value={values.openingHours[day.id].closingTime}
                                                    onChange={handleChange}
                                                />
                                            </Stack>
                                        ))}
                                    </Stack>
                                </Box>}
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        flex: 1,
                                    }}
                                >
                                    <Stack spacing={0}>
                                        {!shabbatOnly && <FormControl
                                            size="small"
                                            fullWidth
                                            error={touched.locationType && Boolean(errors.locationType)}>
                                            <InputLabel>{t('locationsManagement.create.locationType')}</InputLabel>
                                            <Select name="locationType"
                                                value={values.locationType}
                                                onChange={handleChange}>
                                                {LOCATION_TYPE.map((type: { id: string; name: string }) => (
                                                    <MenuItem key={type.id} value={type.id}>
                                                        {type.name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                            <FormHelperText>
                                                {touched.locationType && errors.locationType ? errors.locationType : " "}
                                            </FormHelperText>
                                        </FormControl>}

                                        {!shabbatOnly && <FormControl
                                            size="small"
                                            fullWidth
                                            error={touched.typeOfCuisine && Boolean(errors.typeOfCuisine)}>
                                            <InputLabel>{t('locationsManagement.create.typeOfCuisine')}</InputLabel>
                                            <Select name="typeOfCuisine"
                                                value={values.typeOfCuisine}
                                                onChange={handleChange}>
                                                {TYPE_OF_CUISINE.map(c => (
                                                    <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                                                ))}
                                            </Select>
                                            <FormHelperText>
                                                {touched.typeOfCuisine && errors.typeOfCuisine ? errors.typeOfCuisine : " "}
                                            </FormHelperText>
                                        </FormControl>}
                                        {showsMoneyFields &&
                                            <TextField
                                                name="venueId"
                                                size="small"
                                                placeholder={t('locationsManagement.create.venueId')}
                                                fullWidth
                                                value={values.venueId}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                error={touched.venueId && Boolean(errors.venueId)}
                                                helperText={touched.venueId && errors.venueId ? errors.venueId : " "}
                                            />
                                        }

                                        {showsMoneyFields &&
                                            <TextField
                                                name="subMerchantId"
                                                size="small"
                                                placeholder={t('locationsManagement.create.subMerchantId')}
                                                fullWidth
                                                value={values.subMerchantId}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                error={touched.subMerchantId && Boolean(errors.subMerchantId)}
                                                helperText={touched.subMerchantId && errors.subMerchantId ? errors.subMerchantId : " "}
                                            />
                                        }

                                        {/* Optional, and blank by default. A city being
                                            prepared is offered too - that is how its
                                            restaurants get loaded before it opens. */}
                                        {/* A Chabad House's city is Vello's to set. */}
                                        {!shabbatOnly && <TextField
                                            select
                                            name="cityId"
                                            size="small"
                                            label={t('locationsManagement.create.city')}
                                            fullWidth
                                            value={values.cityId}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            helperText={t('locationsManagement.create.cityHelp')}
                                        >
                                            <MenuItem value="">
                                                {t('locationsManagement.create.cityNone')}
                                            </MenuItem>
                                            {cities.map((city) => (
                                                <MenuItem key={city.id} value={city.id}>
                                                    {city.name}
                                                    {city.isActive
                                                        ? ''
                                                        : ` — ${t('locationsManagement.create.cityPreparing')}`}
                                                </MenuItem>
                                            ))}
                                        </TextField>}

                                        <TextField
                                            name="name"
                                            size="small"
                                            placeholder={t('locationsManagement.create.nameOfLocation')}
                                            fullWidth
                                            value={values.name}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={touched.name && Boolean(errors.name)}
                                            helperText={touched.name && errors.name ? errors.name : " "}
                                        />
                                        <TextField
                                            name="phoneNumber"
                                            size="small"
                                            placeholder={t('locationsManagement.create.phoneNumber')}
                                            fullWidth
                                            value={values.phoneNumber}
                                            onChange={handleChange}
                                            onKeyDown={(e) => {
                                                if (e.key === ' ') {
                                                    e.preventDefault();
                                                }
                                            }}
                                            onBlur={handleBlur}
                                            error={touched.phoneNumber && Boolean(errors.phoneNumber)}
                                            helperText={touched.phoneNumber && errors.phoneNumber ? errors.phoneNumber : " "}
                                        />
                                        <TextField
                                            name="email"
                                            size="small"
                                            placeholder={t('locationsManagement.create.email')}
                                            fullWidth
                                            value={values.email}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={touched.email && Boolean(errors.email)}
                                            helperText={touched.email && errors.email ? errors.email : " "}
                                        />
                                        <StandaloneSearchBox
                                            onLoad={ref => (addressInputRef.current = ref)}
                                            onPlacesChanged={handleOnPlacesChanged}
                                        >
                                            <TextField
                                                name="address"
                                                size="small"
                                                placeholder={t('locationsManagement.create.address')}
                                                fullWidth
                                                value={values.address}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                error={touched.addressObject && Boolean(errors.addressObject) && !isAddressComplete}
                                                sx={{
                                                    '& .MuiFormHelperText-root': {
                                                        color: touched.address && addressError ? 'error.main' : undefined
                                                    }
                                                }}
                                                helperText={addressError && !isAddressComplete ? addressError : " "}
                                            />
                                        </StandaloneSearchBox>

                                        <FormControl
                                            size="small"
                                            fullWidth
                                            error={touched.language && Boolean(errors.language)}>
                                            <InputLabel>{t('locationsManagement.create.language')}</InputLabel>
                                            <Select
                                                name="language"
                                                value={values.language}
                                                onBlur={handleBlur}
                                                onChange={(e) => {
                                                    handleChange(e);
                                                    const langName = LANGUAGE.find(l => l.id === e.target.value)?.id;

                                                    if (translations.length > 0 && !translated) {
                                                        const match = translations.find(t => t.language === langName);
                                                        setFieldValue("description", match?.translation || data.description);
                                                    }
                                                    else {
                                                        console.error("not translated");
                                                    }
                                                }}>
                                                <MenuItem value="" disabled>
                                                    {t('locationsManagement.create.language')}
                                                </MenuItem>
                                                {LANGUAGE.map(c => (
                                                    <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                                                ))}
                                            </Select>

                                            <FormHelperText>
                                                {touched.language && errors.language ? errors.language : " "}
                                            </FormHelperText>
                                        </FormControl>
                                        {showsMoneyFields &&
                                            <TextField
                                                type="number"
                                                name="restaurantFee"
                                                size="small"
                                                placeholder={t('locationsManagement.create.restaurantFee')}
                                                fullWidth
                                                slotProps={{
                                                    htmlInput: { min: 0, max: 100, step: 0.001 },
                                                    input: {
                                                        endAdornment: <InputAdornment position="end">%</InputAdornment>
                                                    }
                                                }}
                                                value={values.restaurantFee}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                error={touched.restaurantFee && Boolean(errors.restaurantFee)}
                                                helperText={touched.restaurantFee && errors.restaurantFee
                                                    ? errors.restaurantFee
                                                    : t('locationsManagement.create.restaurantFeeHelp', 'Delivery commission %: charged on delivery orders. A change starts tomorrow.')}
                                            />
                                        }
                                        {/* The second rate, admins only like the delivery one:
                                            pickup and dine-in orders are charged this. */}
                                        {showsMoneyFields &&
                                            <TextField
                                                type="number"
                                                name="pickupCommissionRate"
                                                size="small"
                                                placeholder={t('locationsManagement.create.pickupCommissionRate', 'Pickup & dine-in commission %')}
                                                fullWidth
                                                slotProps={{
                                                    htmlInput: { min: 0, max: 100, step: 0.01 },
                                                    input: {
                                                        endAdornment: <InputAdornment position="end">%</InputAdornment>
                                                    }
                                                }}
                                                value={values.pickupCommissionRate}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                error={touched.pickupCommissionRate && Boolean(errors.pickupCommissionRate)}
                                                helperText={touched.pickupCommissionRate && errors.pickupCommissionRate
                                                    ? String(errors.pickupCommissionRate)
                                                    : isEditMode
                                                        ? t('locationsManagement.create.pickupCommissionRateHelpEdit', 'Pickup & dine-in commission %: charged on pickup and dine-in orders. A change applies to new orders at once.')
                                                        : t('locationsManagement.create.pickupCommissionRateHelpCreate', 'Pickup & dine-in commission %: charged on pickup and dine-in orders. Leave empty to use the delivery rate.')}
                                                sx={{ mb: 1 }}
                                            />
                                        }

                                        {/* Dine-in, admins only: the customer orders now and
                                            eats at the venue - no courier, no delivery fee. */}
                                        {showsMoneyFields &&
                                            <Box sx={{ border: '1px solid var(--color-secondary)', borderRadius: 'var(--radius-6, 6px)', px: 1.5, py: 1, mb: 2 }}>
                                                <FormControlLabel
                                                    sx={{ alignItems: 'flex-start', m: 0, gap: 1.5 }}
                                                    control={
                                                        <Switch
                                                            name="acceptsDineIn"
                                                            checked={Boolean(values.acceptsDineIn)}
                                                            onChange={(_: any, checked: boolean) => setFieldValue('acceptsDineIn', checked)}
                                                        />
                                                    }
                                                    label={
                                                        <Box>
                                                            <Typography variant="body2" sx={{ fontWeight: 700 }}>Accepts dine-in</Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                Customers can order now and eat here: paid in the app, ready when they arrive. No courier, no delivery fee.
                                                            </Typography>
                                                        </Box>
                                                    }
                                                />
                                            </Box>
                                        }

                                        {/* Pickup, admins only: the customer orders now and
                                            collects at the counter - no courier, no delivery fee. */}
                                        {showsMoneyFields &&
                                            <Box sx={{ border: '1px solid var(--color-secondary)', borderRadius: 'var(--radius-6, 6px)', px: 1.5, py: 1, mb: 2 }}>
                                                <FormControlLabel
                                                    sx={{ alignItems: 'flex-start', m: 0, gap: 1.5 }}
                                                    control={
                                                        <Switch
                                                            name="acceptsPickup"
                                                            checked={Boolean(values.acceptsPickup)}
                                                            onChange={(_: any, checked: boolean) => setFieldValue('acceptsPickup', checked)}
                                                        />
                                                    }
                                                    label={
                                                        <Box>
                                                            <Typography variant="body2" sx={{ fontWeight: 700 }}>{t('locationsManagement.create.acceptsPickup', 'Accepts pickup')}</Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {t('locationsManagement.create.acceptsPickupHelp', 'Customers can order now and collect at the counter: paid in the app, ready when they come. No courier, no delivery fee.')}
                                                            </Typography>
                                                        </Box>
                                                    }
                                                />
                                            </Box>
                                        }

                                        {/* Shabbat meals, admins only. "Shabbat only" is a
                                            Chabad House: it needs the meal pages too, so
                                            switching it on turns hosting on, and switching
                                            hosting off turns it off. */}
                                        {showsMoneyFields &&
                                            <Box sx={{ border: '1px solid var(--color-secondary)', borderRadius: 'var(--radius-6, 6px)', px: 1.5, py: 1, mb: 2 }}>
                                                <FormControlLabel
                                                    sx={{ alignItems: 'flex-start', m: 0, gap: 1.5 }}
                                                    control={
                                                        <Switch
                                                            name="hostsShabbat"
                                                            checked={Boolean(values.hostsShabbat)}
                                                            onChange={(_: any, checked: boolean) => {
                                                                setFieldValue('hostsShabbat', checked);
                                                                if (!checked) setFieldValue('shabbatOnly', false);
                                                            }}
                                                        />
                                                    }
                                                    label={
                                                        <Box>
                                                            <Typography variant="body2" sx={{ fontWeight: 700 }}>Hosts Shabbat meals</Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                Shows the Shabbat meals pages in this venue's panel. Guests see the venue once it has meals this Shabbat.
                                                            </Typography>
                                                        </Box>
                                                    }
                                                />
                                                {values.hostsShabbat &&
                                                    <TextField
                                                        type="number"
                                                        name="shabbatFeePercent"
                                                        label="Shabbat meals fee"
                                                        size="small"
                                                        fullWidth
                                                        sx={{ mt: 1.5 }}
                                                        slotProps={{
                                                            htmlInput: { min: 0, max: 100, step: 0.01, 'aria-label': 'Shabbat meals fee' },
                                                            input: {
                                                                endAdornment: <InputAdornment position="end">%</InputAdornment>
                                                            }
                                                        }}
                                                        value={values.shabbatFeePercent}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        error={touched.shabbatFeePercent && Boolean(errors.shabbatFeePercent)}
                                                        helperText={touched.shabbatFeePercent && errors.shabbatFeePercent
                                                            ? errors.shabbatFeePercent
                                                            : "Vello's total fee on Shabbat registrations, including the 1.4% card fee"}
                                                    />
                                                }
                                                <FormControlLabel
                                                    sx={{ alignItems: 'flex-start', m: 0, mt: 1, gap: 1.5 }}
                                                    control={
                                                        <Switch
                                                            name="shabbatOnly"
                                                            checked={Boolean(values.shabbatOnly)}
                                                            onChange={(_: any, checked: boolean) => {
                                                                setFieldValue('shabbatOnly', checked);
                                                                if (checked) setFieldValue('hostsShabbat', true);
                                                            }}
                                                        />
                                                    }
                                                    label={
                                                        <Box>
                                                            <Typography variant="body2" sx={{ fontWeight: 700 }}>Shabbat only</Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                A Chabad House: hidden from the restaurant list and search, no delivery menu, shown only with its Shabbat meals.
                                                            </Typography>
                                                        </Box>
                                                    }
                                                />
                                            </Box>
                                        }

                                        <TextField
                                            name="description"
                                            label={t('locationsManagement.create.description')}
                                            disabled={!values.language || translated || isTranslating}
                                            multiline
                                            rows={3}
                                            fullWidth
                                            slotProps={{ htmlInput: { maxLength: 400 } }}
                                            value={values.description}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={touched.description && Boolean(errors.description)}
                                            helperText={touched.description && errors.description ? errors.description : ""}
                                            sx={{
                                                '& .MuiInputBase-root':
                                                    { padding: 0 }
                                            }}
                                        />

                                    </Stack>
                                </Box>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        flex: 1,
                                        alignItems: 'center',
                                    }}
                                >
                                    <Box
                                        onDrop={handleDrop}
                                        onDragOver={(e) => e.preventDefault()}
                                        sx={{
                                            width: '100%',
                                            height: 360,
                                            border: '1px dashed var(--color-secondary)',
                                            borderRadius: 'var(--radius-4)',
                                            backgroundColor: 'var(--color-background-alt)',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: 'text.secondary',
                                            fontSize: 'var(--text-xs)',
                                            cursor: 'pointer',
                                            transition: 'background-color 0.2s ease',
                                            '&:hover': {
                                                backgroundColor: 'var(--color-background-drop)',
                                            },
                                        }}
                                        onClick={() => document.getElementById('fileInput')?.click()}
                                    >
                                        <input
                                            id="fileInput"
                                            type="file"
                                            accept="image/*"
                                            hidden
                                            onChange={handleFileSelect}
                                        />

                                        {previewUrl ? (
                                            <Box sx={{ textAlign: 'center' }}>
                                                <img
                                                    src={previewUrl}
                                                    alt="preview"
                                                    style={{
                                                        maxWidth: '100%',
                                                        maxHeight: '360px',
                                                        overflow: 'hidden',
                                                        borderRadius: '4px',
                                                        objectFit: 'fill',
                                                    }}
                                                />
                                            </Box>
                                        ) : (
                                            <>
                                                <Typography sx={{ mb: 0.5 }}>{t('locationsManagement.create.dropFileHere')}</Typography>
                                            </>
                                        )}
                                    </Box>

                                    {fileBlob && (
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            spacing={1}
                                            sx={{ mt: 2 }}
                                        >
                                            <IconButton
                                                icon={<EditIcon />}
                                                onClick={() => {
                                                    if (rawImageSrc) {
                                                        setCropDialogOpen(true);
                                                    } else {
                                                        document.getElementById('fileInput')?.click();
                                                    }
                                                }}
                                            />

                                            <IconButton
                                                icon={<DeleteOutlineIcon />}
                                                onClick={handleRemove}
                                            />
                                        </Stack>
                                    )}

                                    <ImageCropDialog
                                        open={cropDialogOpen}
                                        imageSrc={rawImageSrc}
                                        aspect={16 / 9}
                                        onClose={handleCropCancel}
                                        onCropComplete={handleCropComplete}
                                    />
                                </Box>
                            </Box>
                            <Box
                                sx={{
                                    px: 4,
                                    display: 'flex',
                                    gap: 'var(--space-24)',
                                    justifyContent: 'end',
                                    flexDirection: 'row',
                                }}
                            >
                                <Button
                                    type="button"
                                    variant="contained"
                                    disabled={isTranslating || !values.language || !values.description}
                                    sx={{
                                        borderRadius: 0,
                                        backgroundColor: 'var(--color-primary)',
                                        boxShadow: 'none',
                                        alignSelf: 'flex-end',
                                        width: '95px',
                                        '&:hover': { cursor: 'pointer', backgroundColor: 'var(--color-primary-hover)', boxShadow: 'var(--shadow-1)' },
                                    }}
                                    onClick={async () => {
                                        try {
                                            setIsTranslating(true);
                                            const payload = {
                                                defaultLanguage: values.language,
                                                description: values.description
                                            };
                                            console.log(payload);
                                            const response = await translateService.translateLocation(payload);

                                            const sourceLangName = LANGUAGE.find(l => l.id === values.language)?.name;

                                            const fullTranslations = [
                                                { language: sourceLangName, translatedDescription: values.description },
                                                ...response
                                            ];
                                            setTranslations(fullTranslations);
                                            setIsTranslating(false);
                                            setTranslated(true);
                                            notify('Translation completed', { type: 'success' });
                                        }
                                        catch (error) {
                                            notify(error.response.data.error, { type: 'error' });
                                            console.error(error.response.data.error);
                                            setIsTranslating(false);
                                            Sentry.captureException(error);
                                        }

                                    }}
                                >
                                    {isTranslating ? <CircularProgress size={25} sx={{ color: 'white' }} /> : t('locationsManagement.create.translate')}
                                </Button>
                                <Button
                                    variant="outlined"
                                    type="button"
                                    sx={{ borderRadius: 0, borderColor: 'var(--color-error)', color: 'var(--color-error)' }}
                                >
                                    {t('common.cancel')}
                                </Button>

                                <Button
                                    type="submit"
                                    variant="contained"
                                    disabled={!isValid}
                                    sx={{
                                        borderRadius: 0,
                                        backgroundColor: 'var(--color-primary)',
                                        boxShadow: 'none',
                                        alignSelf: 'flex-end',
                                        width: '127px',
                                        '&:hover': { cursor: 'pointer', backgroundColor: 'var(--color-primary-hover)', boxShadow: 'var(--shadow-1)' },
                                    }}
                                >
                                    {isSaving ? <CircularProgress size={25} sx={{ color: 'white' }} /> : t('common.saveChanges')}
                                </Button>
                            </Box>
                        </Form>
                    )
                }}
            </Formik>
            {/* The venue's own login, so the place publishes its meals itself.
                Admins only, once the venue is saved as hosting Shabbat meals. */}
            {isEditMode && showsMoneyFields && data?.hostsShabbat &&
                <ShabbatHostLogin locationId={Number(id)} shabbatOnly={Boolean(data?.shabbatOnly)} />}
        </>
    );
}